import { useMemo } from "react";
import { Checkbox } from "@/core/components/ui/checkbox";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import type {
  IAiSpreadsheetCell,
  IAiSpreadsheetRegionColumnSetting,
} from "@/payroll/ai-script/types";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import type { SpreadsheetRegionDraft } from "./spreadsheet-region";
import { formatColumnLetter } from "./utils";

const CONTROL_GRID =
  "grid grid-cols-[1fr_64px_64px] items-center gap-2 px-3 py-2";

type ColumnControlKey = "filterable" | "sortable";

const DEFAULT_COLUMN_SETTING: IAiSpreadsheetRegionColumnSetting = {
  filterable: true,
  sortable: true,
  declared_type: null,
};

interface SpreadsheetRegionColumnControlsProps {
  cells: IAiSpreadsheetCell[];
  draft: SpreadsheetRegionDraft;
  onChange: (draft: SpreadsheetRegionDraft) => void;
}

export const SpreadsheetRegionColumnControls = ({
  cells,
  draft,
  onChange,
}: SpreadsheetRegionColumnControlsProps) => {
  const columnNumbers = Array.from(
    { length: draft.end_col - draft.start_col + 1 },
    (_, offset) => draft.start_col + offset,
  );

  // A sheet can contain many cells. Rebuild the header lookup only when the
  // selected header row or loaded cells change.
  const headerLabels = useMemo(
    () =>
      new Map(
        cells
          .filter((cell) => cell.row === draft.header_row)
          .map((cell) => [
            cell.col,
            getAiSpreadsheetCellDisplayValue(cell).trim(),
          ]),
      ),
    [cells, draft.header_row],
  );

  const changeEveryColumn = (key: ColumnControlKey, checked: boolean) =>
    onChange({
      ...draft,
      column_settings: Object.fromEntries(
        columnNumbers.map((column) => {
          const columnKey = String(column);
          return [
            columnKey,
            {
              ...DEFAULT_COLUMN_SETTING,
              ...draft.column_settings[columnKey],
              [key]: checked,
            },
          ];
        }),
      ),
    });

  const changeColumn = (
    column: number,
    key: ColumnControlKey,
    checked: boolean,
  ) =>
    onChange({
      ...draft,
      column_settings: {
        ...draft.column_settings,
        [String(column)]: {
          ...DEFAULT_COLUMN_SETTING,
          ...draft.column_settings[String(column)],
          [key]: checked,
        },
      },
    });

  const allEnabled = {
    filterable: columnNumbers.every(
      (column) => draft.column_settings[String(column)]?.filterable,
    ),
    sortable: columnNumbers.every(
      (column) => draft.column_settings[String(column)]?.sortable,
    ),
  };

  return (
    <Stack gap="none" fullWidth>
      <Stack
        direction="row"
        align="center"
        justify="between"
        fullWidth
        className="pb-2"
      >
        <Typography variant="subtitleW3">Column controls</Typography>
        <Typography variant="bodyW3" textColor="secondary">
          {columnNumbers.length} columns
        </Typography>
      </Stack>

      <Stack
        gap="none"
        fullWidth
        // Override Stack's start alignment so every control row has one width.
        align="stretch"
        className="overflow-hidden rounded-lg border border-stroke-tertiary"
      >
        <div className={cn(CONTROL_GRID, "bg-surface-secondary")}>
          <span className="text-sm font-semibold uppercase tracking-wide text-secondary-text">
            Column
          </span>
          <span className="text-center text-sm font-semibold uppercase tracking-wide text-secondary-text">
            Filter
          </span>
          <span className="text-center text-sm font-semibold uppercase tracking-wide text-secondary-text">
            Sort
          </span>
        </div>

        <div className={cn(CONTROL_GRID, "border-t border-stroke-tertiary")}>
          <Typography variant="bodyW3" textColor="secondary">
            All columns
          </Typography>
          {(["filterable", "sortable"] as const).map((key) => (
            <Stack key={key} direction="row" justify="center" gap="none">
              <Checkbox
                aria-label={`${key === "filterable" ? "Filter" : "Sort"} on every column`}
                checked={allEnabled[key]}
                onCheckedChange={(checked) =>
                  changeEveryColumn(key, checked === true)
                }
              />
            </Stack>
          ))}
        </div>

        {columnNumbers.map((column) => {
          const setting = draft.column_settings[String(column)];
          const label = headerLabels.get(column);
          const columnName = formatColumnLetter(column - 1);

          return (
            <div
              key={column}
              className={cn(CONTROL_GRID, "border-t border-stroke-tertiary")}
            >
              <Stack
                direction="row"
                align="center"
                gap="sm"
                className="min-w-0"
              >
                <span className="w-6 shrink-0 font-mono text-sm text-secondary-text">
                  {columnName}
                </span>
                <span className="truncate text-sm" title={label || undefined}>
                  {label || (
                    <span className="text-secondary-text">(no header)</span>
                  )}
                </span>
              </Stack>
              {(["filterable", "sortable"] as const).map((key) => (
                <Stack key={key} direction="row" justify="center" gap="none">
                  <Checkbox
                    aria-label={`${key === "filterable" ? "Filter" : "Sort"} on ${label || columnName}`}
                    checked={setting?.[key] ?? false}
                    onCheckedChange={(checked) =>
                      changeColumn(column, key, checked === true)
                    }
                  />
                </Stack>
              ))}
            </div>
          );
        })}
      </Stack>
    </Stack>
  );
};
